import ForgotPasswordLink from 'components/ForgotPasswordLink';
import SystemError from 'components/SystemError';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { redirectAfterLogin } from 'utils/redirectUri';
import { removeUsernameFromUrlParams } from 'utils/removeUsernameFromUrlParams';

import {
  Box,
  Button,
  designTokens,
  Flex,
  TextField,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';
import { User } from '@project44/ui-utils';

import { useLoginNonSso } from '../../../hooks/useLoginNonSso';
import EyeHidden from '../../../resources/EyeHidden.svg';
import EyeOpen from '../../../resources/EyeOpen.svg';
import { LoginPageResolverEnum } from '../../../utils/constants';
import { LoginNonSSoFormValidationSchema } from './validationNonSsoSchema';

type LoginNonSsoProps = {
  username: string;
  loginPageHandler: ({
    strategy,
    username,
  }: {
    strategy: LoginPageResolverEnum;
    username: string;
  }) => void;
};

const LoginNonSso = ({
  username,
  loginPageHandler,
}: LoginNonSsoProps): JSX.Element => {
  const { t } = useTranslation();

  const [loginWithoutSsoDataState, loginWithoutSsoDataHandler] =
    useLoginNonSso();

  const [showPassword, setShowPassword] = useState<boolean>();

  const isLoadingOrDisableState: boolean = !!(
    loginWithoutSsoDataState?.isLoading || loginWithoutSsoDataState?.data
  );

  const formik = useFormik({
    initialValues: {
      username: username,
      password: '',
    },
    validationSchema: LoginNonSSoFormValidationSchema,
    validateOnChange: false,
    onSubmit: async (values) => {
      await loginWithoutSsoDataHandler({
        username: values.username,
        password: values.password,
      });
    },
  });

  const switchAccountHandler = () => {
    sessionStorage.removeItem('state');
    removeUsernameFromUrlParams();
    loginPageHandler({
      strategy: LoginPageResolverEnum.LOGIN_SSO,
      username: '',
    });
  };

  useEffect(() => {
    if (
      loginWithoutSsoDataState?.data &&
      !loginWithoutSsoDataState?.isLoading
    ) {
      redirectAfterLogin(loginWithoutSsoDataState?.data as User);
    }
  }, [loginWithoutSsoDataState?.isLoading, loginWithoutSsoDataState?.data]);

  return (
    <Box>
      {loginWithoutSsoDataState?.error && (
        <SystemError statusCode={loginWithoutSsoDataState?.error.statusCode} />
      )}
      <form onSubmit={formik.handleSubmit}>
        <TextField
          css={{ marginBottom: designTokens.spaceSmall }}
          placeholder={t('loginPage.emailInputFieldPlaceholder')}
          label={t('loginPage.emailInputFieldLabel')}
          value={formik.values.username}
          isDisabled={true}
          isRequired={true}
        />
        <Box css={{ position: 'relative' }}>
          <TextField
            data-testid="password-input"
            placeholder={t('loginPage.passwordInputFieldPlaceholder')}
            helperText={formik.errors.password}
            label={t('loginPage.passwordInputFieldLabel')}
            inputProps={{
              type: showPassword ? 'text' : 'password',
              autoComplete: 'current-password',
            }}
            value={formik.values.password}
            isDisabled={isLoadingOrDisableState}
            validationState={formik.errors.password ? 'invalid' : 'valid'}
            onChange={(inputValue: string) => {
              formik.errors.password && formik.setErrors({ password: '' });
              formik.setFieldValue('password', inputValue);
            }}
          />
          <img
            style={{
              position: 'absolute',
              right: '0.6rem',
              top: '2.3rem',
              cursor: 'pointer',
            }}
            data-testid="show-password"
            onClick={() => setShowPassword(!showPassword)}
            src={!showPassword ? EyeOpen : EyeHidden}
          />
          <ForgotPasswordLink
            style={{
              color: designTokens.colorTextSecondary,
              position: 'absolute',
              right: '0',
              top: designTokens.spaceXSmall,
              cursor: 'pointer',
            }}
            linkText={t('loginPage.forgotPasswordLink')}
            username={username}
          />
        </Box>
        <Flex gap="small" css={{ marginTop: designTokens.spaceMedium }}>
          <Button
            variant="secondary"
            isDisabled={isLoadingOrDisableState}
            onClick={switchAccountHandler}
          >
            {t('loginPage.switchAccount')}
          </Button>
          <Button
            type="submit"
            variant="brand"
            isDisabled={isLoadingOrDisableState}
            css={{
              width: '100%',
              position: 'relative',
            }}
          >
            {isLoadingOrDisableState
              ? t('signInProcessStatus.signingIn')
              : t('loginPage.logIn')}
          </Button>
        </Flex>
      </form>
    </Box>
  );
};

export default LoginNonSso;
